/**
 * Core-100 R4 wave 3 (CORE-AZ Prov / D) — the ONE reminder template renderer (send-reminder on the server; the
 * Reminder Settings preview in the browser through `src/utils/templateRenderer.ts`).
 *
 * The values substituted into a shop's SMS / email template come from records other people typed — the customer's
 * name, the vehicle, the job names, the shop name. Before this module both copies rendered with
 * `template.replace(new RegExp('{key}', 'g'), value)` once per variable, which is an injection in two ways:
 *
 *   1. replacement patterns — `String.prototype.replace` interprets `$&`, `` $` ``, `$'` and `$1` in the VALUE: a
 *      customer named `$'` copied the rest of the shop's template into the message, `` $` `` everything before it;
 *   2. second-order expansion — the variables were substituted one after another, so a value CONTAINING `{vehicle}`
 *      or `{appointment_time}` (a customer named "{appointment_time}") was expanded by the next pass: text typed into
 *      a record could pull other fields into the body.
 *
 * Rendering is now ONE pass over the template's own placeholders with a replacer function (the value is inserted
 * literally and never re-scanned), and each value is reduced to one line of printable text: control characters
 * (CR / LF / TAB / NUL …) become a single space — a value cannot break an email subject onto a second header line or
 * forge extra lines into an SMS — and it is capped in length. A placeholder that is not a known variable is left as
 * written (the settings form already refuses unknown variables). Email is sent as `text` (never HTML) by the
 * provider seam, so no markup is interpreted.
 */

export interface ReminderTemplateVariables {
  customer_name: string;
  vehicle: string;
  shop_name: string;
  appointment_time: string;
  services: string;
  appointment_type: string;
}

export const REMINDER_TEMPLATE_KEYS: ReadonlyArray<keyof ReminderTemplateVariables> = [
  'customer_name', 'vehicle', 'shop_name', 'appointment_time', 'services', 'appointment_type',
];

/** The longest a single substituted value may be (a whole job list still fits; a pasted essay does not). */
export const REMINDER_TEMPLATE_VALUE_MAX = 200;

/* eslint-disable no-control-regex -- matching control characters is the point (they are removed) */
// deno-lint-ignore no-control-regex
const CONTROL_CHARS = /[\u0000-\u001F\u007F-\u009F\u2028\u2029]+/g;
/* eslint-enable no-control-regex */

/** One line of printable text: control characters collapse to a space, trimmed, capped. */
export function sanitizeReminderTemplateValue(value: unknown): string {
  const s = String(value ?? '').replace(CONTROL_CHARS, ' ').replace(/ {2,}/g, ' ').trim();
  return s.length > REMINDER_TEMPLATE_VALUE_MAX ? `${s.slice(0, REMINDER_TEMPLATE_VALUE_MAX - 1)}…` : s;
}

const PLACEHOLDER = /\{([a-z_]+)\}/g;

/** Render once: each known `{key}` is replaced by its sanitized value, literally; nothing substituted is re-scanned. */
export function renderReminderTemplate(template: string, variables: ReminderTemplateVariables): string {
  const known = new Set<string>(REMINDER_TEMPLATE_KEYS as ReadonlyArray<string>);
  return String(template ?? '').replace(PLACEHOLDER, (whole: string, key: string) =>
    known.has(key) ? sanitizeReminderTemplateValue((variables as unknown as Record<string, unknown>)[key]) : whole);
}
